import {
	LinearProgress,
	Switch,
	Button,
	Icon,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	DialogContentText,
	TextField,
	Grid,
} from '@material-ui/core';
import React from 'react';
import { connect } from '@optum-uhone-hmkts/rise';
import { HeaderBarComponent } from '../../components/Layout/HeaderBar';
import { themePalette } from '../../utilities/branding';
import { AppState } from '../../reducers/index';
import { navigateTo } from '../../actions/navigation_actions';
import { BasePageContainer } from '../../components/Layout/BasePage';
import { PersistentNotifications } from '../../components/notifications/notificationPersistent';
import {
	DisableCalendarSync,
	CalendarSignInCode,
	VerifyCalendarSignIn,
	VerifyCalendarSignInParams,
} from '../../actions/calendar_actions';
import { isNullOrUndefinedOrEmpty } from '../../utilities/utilities';
import { User } from '../../reducers/user_reducer';
import {
	StoreCalendarSettings,
	getUserById,
} from '../../actions/user_actions';
import { CalendarState } from '../../reducers/calendar_reducer';
import { QueueSnackbar, SnackbarProps } from '../../actions/snackbar_actions';
import { copyToClipboard } from '../../utilities/is_mobile';
import { getSnackbarSuccessProps, getSnackbarErrorProps } from '../../utilities/snackbar_util';
import { Dispatch } from '@reduxjs/toolkit';
import { openExternalLink } from '../../utilities';
import { connectOffice365Calendar } from '../../utilities/office365_calendar';
import { DesktopPadding } from '../../components/Layout/desktop_padding';
import { ToggleCard } from '../../components/higher_order_component/wrap_in_card';
import { SimpleListItem } from '../../components/utility/simple_list_item';
import DateRangeIcon from '@material-ui/icons/DateRange';
import PhoneIcon from '@material-ui/icons/Phone';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import { isOffice365Experience } from '../../utilities/agent_util';
import FileCopy from '@material-ui/icons/FileCopy';

interface StateProps {
	calendarState: CalendarState;
	user: User;
	isLoading: boolean;
}
interface DispatchProps {
	navigateTo: (route: string) => void;
	calendarSignInCode: () => void;
	verifyCalendarSignIn: (verifyCalendarSignInDto: VerifyCalendarSignInParams) => void;
	storeCalendarSettings: (user: User) => void;
	disableCalendarSync: () => void;
	getUser: (id: string, sync?: boolean) => void;
    queueSnackbar: (snackbarProps: SnackbarProps) => void;
}
type CalendarSyncPageProps = StateProps & DispatchProps;

interface CalendarSyncState {
	isSignInDialogOpen: boolean;
	isDisableDialogOpen: boolean;
	syncCalendar: boolean;
	appointmentsEnabled: boolean;
	phoneCallsEnabled: boolean;
	tasksEnabled: boolean;
}

class CalendarSyncPage extends React.Component<
	CalendarSyncPageProps,
	CalendarSyncState
	> {
	timer: any;
	signInStarted: boolean;
	constructor(props: CalendarSyncPageProps) {
		super(props);
		this.state = {
			isSignInDialogOpen: false,
			isDisableDialogOpen: false,
			syncCalendar: this.props.user.calendarConnected || false,
			appointmentsEnabled: this.props.user.syncCalendarAppointments || false,
			phoneCallsEnabled: this.props.user.syncCalendarPhoneCalls || false,
			tasksEnabled: this.props.user.syncCalendarTasks || false,
		};
	}

	componentDidMount() {
		this.props.getUser(this.props.user.id, false);
		this.handleOffice365RedirectResult();
	}

	// Handles the return from the installed-PWA redirect flow: the API callback navigates back to
	// /settings/calendar?o365=success|error. Popup mode does not use this (it resolves in-page).
	handleOffice365RedirectResult = () => {
		const url = new URL(window.location.href);
		const result = url.searchParams.get('o365');
		if (result !== 'success' && result !== 'error') {
			return;
		}

		if (result === 'success') {
			this.props.getUser(this.props.user.id, false);
			this.props.queueSnackbar(getSnackbarSuccessProps('Office 365 Calendar successfully connected'));
		} else {
			this.props.queueSnackbar(getSnackbarErrorProps('Failed to connect Office 365 Calendar'));
		}

		// Strip the o365 param so a refresh or back-navigation does not re-fire the snackbar.
		url.searchParams.delete('o365');
		window.history.replaceState(null, '', url.pathname + url.search + url.hash);
	};

	componentDidUpdate(prevProps: CalendarSyncPageProps) {
		if (prevProps.user.syncCalendarAppointments !== this.props.user.syncCalendarAppointments ||
			prevProps.user.syncCalendarPhoneCalls !== this.props.user.syncCalendarPhoneCalls ||
			prevProps.user.syncCalendarTasks !== this.props.user.syncCalendarTasks) {
			this.setState({
				appointmentsEnabled: this.props.user.syncCalendarAppointments || false,
				phoneCallsEnabled: this.props.user.syncCalendarPhoneCalls || false,
				tasksEnabled: this.props.user.syncCalendarTasks || false,
			});
		}

		// The Google device-code verify flow now persists the refresh token server-side, so a
		// successful verification is reflected by refreshing the user. Once calendarConnected
		// flips true, the enabled state and sync toggles update. The verify saga owns the
		// "successfully verified" snackbar, so it is not duplicated here.
		if (!prevProps.calendarState.verifyCalendarSignInDone &&
			this.props.calendarState.verifyCalendarSignInDone) {
			this.signInStarted = false;
			this.props.getUser(this.props.user.id, false);
		}
	}

	componentWillUnmount() {
		window.clearInterval(this.timer);
	}

	createCalendarSignInCodeDisplay = () => {
		const {calendarSignInCodeDone, calendarSignInCode} = this.props.calendarState;
		if (calendarSignInCodeDone === 100) {
			const _calendarSignInCode = String(calendarSignInCode);
			return isNullOrUndefinedOrEmpty(_calendarSignInCode) ? 'Error' : _calendarSignInCode;
		} else {
			return '';
		}
	};

	disableCalendarSync = () => {
		this.closeDisableDialog();
		window.clearInterval(this.timer);
		this.props.disableCalendarSync();
	};

	closeSignInDialog = () => {
		this.setState({ isSignInDialogOpen: false });
	};

	closeDisableDialog = () => {
		this.setState({ isDisableDialogOpen: false });
	};

	openDialog = () => {
		if (
			!this.props.user.calendarConnected
		) {
			if (isOffice365Experience()) {
				// Office 365 uses an authorization-code + PKCE popup; the API persists the refresh
				// token server-side, so there is no device code to display or poll for.
				this.startOffice365Registration();
			} else {
				this.signInStarted = true;
				this.setState({ isSignInDialogOpen: true, isDisableDialogOpen: false });
				this.props.calendarSignInCode();
			}
		} else {
			this.setState({ isDisableDialogOpen: true, isSignInDialogOpen: false });
		}
	};

	startOffice365Registration = async () => {
		this.signInStarted = true;
		this.setState({ isSignInDialogOpen: false, isDisableDialogOpen: false });
		try {
			await connectOffice365Calendar();
			// The refresh token was persisted server-side; refresh the user so the enabled state
			// and sync toggles reflect the new connection.
			this.props.getUser(this.props.user.id, false);
			this.props.queueSnackbar(getSnackbarSuccessProps('Office 365 Calendar successfully connected'));
		} catch (error) {
			this.props.queueSnackbar(getSnackbarErrorProps(
				(error && (error as Error).message) || 'Failed to connect Office 365 Calendar'
			));
		} finally {
			this.signInStarted = false;
		}
	};

	verifyCalendarSignIn = () => {
		const { calendarDeviceCode, calendarPollInterval, calendarPollTimeout } = this.props.calendarState;
		if (calendarDeviceCode) {
			this.signInStarted = true;
			const verifyCalendarSignInDto = {
				calendarDeviceCode, calendarPollInterval, calendarPollTimeout 
			} as VerifyCalendarSignInParams;
			this.props.verifyCalendarSignIn(verifyCalendarSignInDto);
		}
	}

	handleSignInClick = () => {
		this.closeSignInDialog();
		this.navigateToGoogle();
	};

	navigateToGoogle = () => {
		const { calendarVerificationUrl } = this.props.calendarState;
		this.verifyCalendarSignIn();
		openExternalLink(calendarVerificationUrl);
	};

	savePreferences = () => {
		const newUserSettings = this.props.user;
		newUserSettings.syncCalendarAppointments = this.state.appointmentsEnabled;
		newUserSettings.syncCalendarPhoneCalls = this.state.phoneCallsEnabled;
		newUserSettings.syncCalendarTasks = this.state.tasksEnabled;
		this.props.storeCalendarSettings(newUserSettings);
	};

	toggleAppointmentsEnabled = () => {
		this.setState({ appointmentsEnabled: !this.state.appointmentsEnabled });
	};

	togglePhoneCallsEnabled = () => {
		this.setState({ phoneCallsEnabled: !this.state.phoneCallsEnabled });
	};

	toggleTasksEnabled = () => {
		this.setState({ tasksEnabled: !this.state.tasksEnabled });
	};

	createEnableDisableDisplay = () => {
		return this.props.user.calendarConnected ? 'Enabled' : 'Disabled';
	};

	renderSignInDialog() {
		const {calendarSignInCodeDone} = this.props.calendarState;
        const calendarCode = this.createCalendarSignInCodeDisplay();
		return (
			<Dialog open={this.state.isSignInDialogOpen}>
				{calendarSignInCodeDone != 100 ? (
					<LinearProgress />
				) : null}
				<DialogTitle>
					{isOffice365Experience() ? 'Sync To-Dos with Office 365 Calendar' : 'Sync To-Dos with Google Calendar'}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{isOffice365Experience() ? 'Copy this code for Microsoft Sign In:' : 'Copy this code for Google Sign In:'}
					</DialogContentText>
					<TextField
						style={{ color: themePalette.primary_text }}
						id="googleCal"
						value={calendarCode}
					/>
					<Icon
						onClick={() => {
                            copyToClipboard(calendarCode || '');
                            this.props.queueSnackbar(getSnackbarSuccessProps('Copied URL to clipboard', 2000));
                        }}
					>
						<FileCopy />
					</Icon>
				</DialogContent>
				<DialogActions>
					<Button
						color="secondary"
						onClick={this.closeSignInDialog}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={this.handleSignInClick}
						disabled={isNullOrUndefinedOrEmpty(String(calendarSignInCodeDone))}
						style={{ backgroundColor: themePalette.accept_button }}
					>
						Sign In
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	renderDisableDialog() {
		return (
			<Dialog open={this.state.isDisableDialogOpen}>
				<DialogTitle>
					{'Disable Calendar Sync'}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{isOffice365Experience()
							? 'Are you sure you want to discontinue syncing with Office 365 Calendar?'
							: 'Are you sure you want to discontinue syncing with Google Calendar?'}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						color="secondary"
						onClick={this.closeDisableDialog}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={this.disableCalendarSync}
						style={{ backgroundColor: themePalette.delete_remove_reject_button }}
					>
						Disable
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	render() {
		return (
			<BasePageContainer
				topComponent={
					<HeaderBarComponent
						title="Settings"
						isLoading={
							this.signInStarted == true || this.props.isLoading
						}
					/>
				}
			>
				<PersistentNotifications />
				<DesktopPadding>
					<ToggleCard title={isOffice365Experience() ? 'Office 365 Calendar Settings' : 'Google Calendar Settings'} noPadding>
						<SimpleListItem
							title={isOffice365Experience() ? 'Office 365 Calendar Sync' : 'Google Calendar Sync'}
							subtitle={this.createEnableDisableDisplay()}
							secondaryAction={
								<Switch
									color="primary"
									onClick={this.openDialog}
									checked={this.props.user.calendarConnected || false}
									disabled={this.signInStarted == true && !this.props.user.calendarConnected}
								/>
							}
						/>
						{this.props.user.calendarConnected && (
								<>
									<SimpleListItem subtitle={'To-Dos'} />
									<SimpleListItem 
										title={'Appointments'}
										icon={<DateRangeIcon/>}
										secondaryAction={
											<Switch
												color="primary"
												checked={this.state.appointmentsEnabled}
												onChange={this.toggleAppointmentsEnabled}
											/>
										}
									/>
									<SimpleListItem 
										title={'Phone Calls'}
										icon={<PhoneIcon/>}
										secondaryAction={
											<Switch
												color="primary"
												checked={this.state.phoneCallsEnabled}
												onChange={this.togglePhoneCallsEnabled}
											/>
										}
									/>
									<SimpleListItem 
										title={'Tasks'}
										icon={<EventAvailableIcon/>}
										secondaryAction={
											<Switch
												color="primary"
												checked={this.state.tasksEnabled}
												onChange={this.toggleTasksEnabled}
											/>
										}
									/>
									<Grid container>
										<Button
											variant="contained"
											color="primary"
											fullWidth
											style={{ backgroundColor: themePalette.accept_button, margin: 20 }}
											onClick={this.savePreferences}
										>
											<div>Sync Now</div>
										</Button>
									</Grid>
								</>
							)
						}
					</ToggleCard>
				</DesktopPadding>
				{this.renderDisableDialog()}
				{this.renderSignInDialog()}
			</BasePageContainer>
		);
	}
}

const mapStateToProps = (state: AppState): StateProps => ({
	calendarState: state.calendar,
	user: state.user,
	isLoading: state.activity.isLoading,
});
const mapDispatchToProps = (dispatch: Dispatch): Partial<CalendarSyncPageProps> => ({
	navigateTo: (route: string) =>
		dispatch(navigateTo(route)),
	calendarSignInCode: () => dispatch(CalendarSignInCode.started()),
	verifyCalendarSignIn: (verifyCalendarSignInDto: VerifyCalendarSignInParams) => 
		dispatch(VerifyCalendarSignIn.started(verifyCalendarSignInDto)),
	storeCalendarSettings: (user: User) =>
		dispatch(StoreCalendarSettings.started(user)),
	disableCalendarSync: () =>
		dispatch(DisableCalendarSync.started(undefined)),
	getUser: (id: string, sync?: boolean) => dispatch(getUserById(id, sync)),
    queueSnackbar: (snackbarProps: SnackbarProps) => dispatch(QueueSnackbar(snackbarProps)),
});

export const CalendarSyncContainer = connect(
	mapStateToProps,
	mapDispatchToProps,
	true
)(CalendarSyncPage);
